import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Alert, Box, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../../context/AuthContext';
import { useClientMessagingWebSocket } from '../../../hooks/useClientMessagingWebSocket';
import { messagingService } from '../../../services/messaging.service';
import AppointmentConversationSummary from '../../client/messaging/shared/AppointmentConversationSummary';
import MessagingConversationList from '../../client/messaging/shared/MessagingConversationList';
import MessagingThreadView from '../../client/messaging/shared/MessagingThreadView';

const noop = () => {};

const normalizeItems = (data) => (
  Array.isArray(data) ? data : data?.results || []
);

const getUnreadTotal = (items) => items.reduce(
  (total, item) => total + Number(item.unread_count || 0),
  0,
);

const ProClientChatPanel = ({
  salonId,
  onUnreadCountChange = noop,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [query, setQuery] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const currentSalonRef = useRef(salonId);
  const selectedIdRef = useRef(selectedId);
  const listRequestRef = useRef(0);
  const detailRequestRef = useRef(0);
  const translateRef = useRef(t);

  translateRef.current = t;
  selectedIdRef.current = selectedId;

  const publishConversations = useCallback((updater) => {
    setConversations((current) => {
      const next = typeof updater === 'function' ? updater(current) : updater;
      onUnreadCountChange(getUnreadTotal(next));
      return next;
    });
  }, [onUnreadCountChange]);

  const loadList = useCallback(async () => {
    if (!salonId) {
      publishConversations([]);
      setLoading(false);
      return;
    }

    const requestId = ++listRequestRef.current;
    setLoading(true);
    try {
      const response = await messagingService.listSalonClientConversations(
        salonId,
        {},
      );
      if (
        requestId !== listRequestRef.current
        || Number(currentSalonRef.current) !== Number(salonId)
      ) return;
      publishConversations(normalizeItems(response.data));
      setError('');
    } catch (_requestError) {
      if (requestId !== listRequestRef.current) return;
      setError(translateRef.current(
        'client_messaging.load_error',
        'Impossible de charger les discussions.',
      ));
    } finally {
      if (requestId === listRequestRef.current) setLoading(false);
    }
  }, [publishConversations, salonId]);

  useEffect(() => {
    currentSalonRef.current = salonId;
    listRequestRef.current += 1;
    detailRequestRef.current += 1;
    setSelectedId(null);
    setDetail(null);
    setMessage('');
    setError('');
    loadList();
  }, [loadList, salonId]);

  useEffect(() => {
    if (!selectedId || !salonId) return;
    const conversationId = selectedId;
    const activeSalonId = salonId;
    const requestId = ++detailRequestRef.current;
    setDetail(null);
    setError('');

    const loadDetail = async () => {
      try {
        const response = await messagingService.retrieveClientConversation(
          conversationId,
          { salon: activeSalonId },
        );
        if (
          requestId !== detailRequestRef.current
          || Number(currentSalonRef.current) !== Number(activeSalonId)
          || Number(selectedIdRef.current) !== Number(conversationId)
        ) return;
        setDetail(response.data);
        await messagingService.markClientConversationRead(
          conversationId,
          { salon: activeSalonId },
        );
        publishConversations((items) => items.map((item) => (
          Number(item.id) === Number(conversationId)
            ? { ...item, unread_count: 0 }
            : item
        )));
      } catch (_requestError) {
        if (requestId !== detailRequestRef.current) return;
        setError(translateRef.current(
          'client_messaging.load_error',
          'Impossible de charger la discussion.',
        ));
      }
    };

    loadDetail();
  }, [publishConversations, salonId, selectedId]);

  const handleSocketMessage = useCallback((data) => {
    if (!data) return;
    if (data.type === 'new_conversation') {
      loadList();
      return;
    }
    if (data.type !== 'new_message' || !data.message) return;

    const incoming = data.message;
    const isOpen = Number(selectedIdRef.current)
      === Number(incoming.conversation_id);
    publishConversations((items) => items.map((item) => (
      Number(item.id) === Number(incoming.conversation_id)
        ? {
          ...item,
          last_message: incoming,
          unread_count: isOpen
            ? 0
            : Number(item.unread_count || 0) + 1,
        }
        : item
    )));

    if (!isOpen) return;
    setDetail((current) => {
      if (!current) return current;
      const currentMessages = current.messages || [];
      if (currentMessages.some((item) => item.id === incoming.id)) {
        return current;
      }
      return {
        ...current,
        messages: [...currentMessages, incoming],
      };
    });
    messagingService.markClientConversationRead(
      incoming.conversation_id,
      { salon: currentSalonRef.current },
    ).catch(noop);
  }, [loadList, publishConversations]);

  const { status: socketStatus, sendJson } = useClientMessagingWebSocket({
    enabled: Boolean(salonId && user?.token),
    token: user?.token,
    onMessage: handleSocketMessage,
  });

  useEffect(() => {
    if (!selectedId || socketStatus !== 'open') return;
    sendJson({
      action: 'join_conversation',
      conversation_id: selectedId,
      salon_id: salonId,
    });
  }, [salonId, selectedId, sendJson, socketStatus]);

  const send = async () => {
    const content = message.trim();
    if (!content || !selectedId || !salonId || sending) return;
    setMessage('');
    setSending(true);
    setError('');

    const optimistic = {
      id: `tmp-${Date.now()}`,
      conversation_id: selectedId,
      sender_type: 'salon',
      content,
      created_at: new Date().toISOString(),
    };
    setDetail((current) => current ? {
      ...current,
      messages: [...(current.messages || []), optimistic],
    } : current);

    try {
      const sent = sendJson({
        action: 'send_message',
        conversation_id: selectedId,
        salon_id: salonId,
        content,
      });
      if (!sent) {
        const response = await messagingService.sendClientConversationMessage(
          selectedId,
          content,
          { salon: salonId },
        );
        setDetail((current) => current ? {
          ...current,
          messages: [
            ...(current.messages || []).filter(
              (item) => item.id !== optimistic.id,
            ),
            response.data,
          ],
        } : current);
      }
      loadList();
    } catch (_requestError) {
      setMessage(content);
      setDetail((current) => current ? {
        ...current,
        messages: (current.messages || []).filter(
          (item) => item.id !== optimistic.id,
        ),
      } : current);
      setError(t(
        'client_messaging.send_error',
        'Impossible d’envoyer le message.',
      ));
    } finally {
      setSending(false);
    }
  };

  if (loading && !conversations.length) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', height: '100%' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error && !conversations.length && !detail) {
    return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  }

  return (
    <Box sx={{ height: '100%', minHeight: 0 }}>
      {!detail ? (
        <MessagingConversationList
          conversations={conversations}
          selectedId={selectedId}
          query={query}
          onQueryChange={setQuery}
          onSelect={(conversation) => setSelectedId(conversation.id)}
          getIdentity={(conversation) => (
            conversation.client?.display_name || 'Cliente'
          )}
          getPreview={(conversation) => (
            conversation.last_message?.content
            || conversation.subject
            || t(
              `client_messaging.kind_${conversation.kind}`,
              conversation.kind || 'Discussion',
            )
          )}
          emptyTitle="Aucun message client"
          emptyDescription="Les nouvelles demandes de vos clientes apparaîtront ici."
        />
      ) : (
        <MessagingThreadView
          identity={detail.client?.display_name || 'Cliente'}
          subtitle={detail.assigned_to?.display_name || 'Discussion cliente'}
          status={detail.status}
          messages={detail.messages || []}
          ownSenderType="salon"
          draft={message}
          onDraftChange={setMessage}
          onSend={send}
          sending={sending}
          error={error}
          context={
            detail.kind === 'existing_appointment' && detail.appointment ? (
              <AppointmentConversationSummary
                appointment={detail.appointment}
              />
            ) : null
          }
          onBack={() => {
            setSelectedId(null);
            setDetail(null);
            setMessage('');
            setError('');
          }}
        />
      )}
    </Box>
  );
};

export default ProClientChatPanel;
